import React from 'react';
import {Chip, Stack, Typography} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {formatValue, getNestedValue} from '../utils';
import {REPORT_STATUS} from '../constants';
import ValidationStatusIcons from './ValidationStatusIcons';
import ProductTitleLink from '../../../../../features/products/components/common/ProductTitleLink';
import AuthorChip from '../../../../../features/products/components/common/AuthorChip';

/**
 * Renders a single row in the reports table
 * @param {Object} props
 * @param {Object} props.report - Report data
 * @param {Array} props.columns - Column configuration
 * @returns {JSX.Element}
 */
const ReportRow = ({report, columns}) => {

    // Check if this is a disclosure
    const isDisclosure = report.productType?.value === 'disclosure' ||
        report.productType?.value === 'ndp1_disclosure';

    const renderCell = (column) => {
        const value = getNestedValue(report, column.field);

        // Render Product name as a link
        if (column.field === 'title') {
            const linkPath = isDisclosure
                ? `/disclosures/${report.id}`
                : `/products/${report.id}`;

            return (
                <ProductTitleLink
                    to={linkPath}
                    title={value}
                    flex={column.flex}
                />
            );
        }

        // Render Author as a chip with avatar
        if (column.field === 'userDisplayName') {
            return (
                <AuthorChip
                    userId={report.userId}
                    userDisplayName={value}
                    flex={column.flex}
                    avatarProfile={report.authorProfiles?.[report.userId] ?? null}
                />
            );
        }

        // Special rendering for status column
        if (column.field === 'status') {
            if (value === REPORT_STATUS.AWAITING_REVIEW) {
                return (
                    <Stack flex={column.flex}>
                        <ValidationStatusIcons
                            validation={report.validation}
                            productTypeValue={report.productType?.value || null}
                        />
                    </Stack>
                );
            }
            if (value === REPORT_STATUS.APPROVED) {
                return (
                    <Stack flex={column.flex}>
                        <Chip
                            icon={<CheckCircleIcon/>}
                            label="Approved"
                            color="success"
                            size="small"
                            variant="outlined"
                        />
                    </Stack>
                );
            }
        }

        // Default rendering
        return (
            <Typography flex={column.flex}>
                {formatValue(value)}
            </Typography>
        );
    };

    return (
        <Stack
            direction="row"
            spacing={2}
            px={2}
            py={2}
            bgcolor="white"
            sx={{
                '&:hover': {
                    backgroundColor: '#f9f9f9',
                },
            }}
        >
            {columns.map((column, index) => (
                <React.Fragment key={index}>
                    {renderCell(column)}
                </React.Fragment>
            ))}
        </Stack>
    );
};

export default ReportRow;
